import React, { useState } from 'react';
import axios from 'axios';
import MessageBox from './MessageBox';

const InputField = ({ 
  type, 
  placeholder, 
  value, 
  onChange, 
  icon, 
  focused, 
  onFocus, 
  onBlur,
  showToggle = false,
  onToggle = null,
  showStrength = false,
  passwordStrength = 0,
  getPasswordStrengthColor,
  getPasswordStrengthText
}) => (
  <div className="relative group">
    <div className="relative">
      <div className={`absolute left-3 top-1/2 transform -translate-y-1/2 transition-all duration-200 z-10 ${
        focused || value ? 'text-[#10c9c0]' : 'text-gray-400 group-hover:text-gray-600'
      }`}>
        {icon}
      </div>
      
      <input
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        onFocus={onFocus}
        onBlur={onBlur}
        className={`w-full pl-10 pr-${showToggle ? '10' : '4'} py-3 border-2 rounded-xl bg-gray-50/50 transition-all duration-200 placeholder-gray-400 focus:placeholder-gray-300 ${
          focused || value
            ? 'border-[#10c9c0] bg-white shadow-lg shadow-[#10c9c0]/10 focus:outline-none'
            : 'border-gray-200 hover:border-gray-300 focus:border-[#10c9c0] focus:bg-white focus:outline-none focus:shadow-lg focus:shadow-[#10c9c0]/10'
        }`}
        required
      />
      
      {showToggle && (
        <button
          type="button"
          onClick={onToggle}
          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors duration-200 z-10"
        >
          {type === 'password' ? (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
          ) : (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
            </svg>
          )}
        </button>
      )}
    </div>

    {/* Password Strength Indicator */}
    {showStrength && value && (
      <div className="mt-2 space-y-1">
        <div className="flex justify-between items-center text-xs">
          <span className="text-gray-500">Password strength</span>
          <span className={`font-medium ${
            passwordStrength <= 25 ? 'text-red-500' :
            passwordStrength <= 50 ? 'text-orange-500' :
            passwordStrength <= 75 ? 'text-yellow-500' : 'text-green-500'
          }`}>
            {getPasswordStrengthText()}
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
          <div 
            className={`h-full transition-all duration-300 ${getPasswordStrengthColor()}`}
            style={{ width: `${passwordStrength}%` }}
          />
        </div>
      </div>
    )}
  </div>
);

const ForgetPasswordForm = ({ onToggle }) => {
  const [email, setEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [emailFocused, setEmailFocused] = useState(false);
  const [codeFocused, setCodeFocused] = useState(false);
  const [newPasswordFocused, setNewPasswordFocused] = useState(false);
  const [confirmPasswordFocused, setConfirmPasswordFocused] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);

  // Password strength calculator
  const calculatePasswordStrength = (password) => {
    let strength = 0;
    if (password.length >= 8) strength += 25;
    if (/[a-z]/.test(password)) strength += 25;
    if (/[A-Z]/.test(password)) strength += 25;
    if (/[0-9]/.test(password)) strength += 25;
    return strength;
  };

  const handleNewPasswordChange = (e) => {
    const password = e.target.value;
    setNewPassword(password);
    setPasswordStrength(calculatePasswordStrength(password));
  };

  const getPasswordStrengthColor = () => {
    if (passwordStrength <= 25) return 'bg-red-500';
    if (passwordStrength <= 50) return 'bg-orange-500';
    if (passwordStrength <= 75) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const getPasswordStrengthText = () => {
    if (passwordStrength <= 25) return 'Weak';
    if (passwordStrength <= 50) return 'Fair';
    if (passwordStrength <= 75) return 'Good';
    return 'Strong';
  };

  const requestVerificationCode = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage('');
    
    try {
      await axios.post('https://app.akiya2.com/request-password-reset', { email });
      setMessage('Verification code sent to your email.');
      setMessageType('success');
      setStep(2);
    } catch (error) {
      setMessage(error.response?.data?.detail || 'Email not found. Please check your email address.');
      setMessageType('error');
      console.error('Error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const verifyCode = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage('');
    
    try {
      await axios.post('https://app.akiya2.com/verify-reset-code', { email, code: verificationCode });
      setMessage('Code verified successfully. Set your new password.');
      setMessageType('success');
      setStep(3);
    } catch (error) {
      setMessage(error.response?.data?.detail || 'Invalid verification code. Please try again.');
      setMessageType('error');
      console.error('Error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const resetPassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      setMessage('Passwords do not match.');
      setMessageType('error');
      return;
    }
    
    setIsLoading(true);
    setMessage('');
    
    try {
      await axios.post('https://app.akiya2.com/reset-password', { 
        email, 
        code: verificationCode,
        newPassword 
      });
      setMessage('Password has been reset successfully. You can now login with your new password.');
      setMessageType('success');
      setEmail('');
      setVerificationCode('');
      setNewPassword('');
      setConfirmPassword('');
      setTimeout(() => {
        onToggle();
      }, 3000);
    } catch (error) {
      setMessage(error.response?.data?.detail || 'An error occurred. Please try again.');
      setMessageType('error');
      console.error('Error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const StepIndicator = () => (
    <div className="flex items-center justify-center mb-8">
      <div className="flex items-center space-x-4">
        {[1, 2, 3].map((stepNumber) => (
          <React.Fragment key={stepNumber}>
            <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 transition-all duration-300 ${
              step >= stepNumber 
                ? 'bg-[#10c9c0] border-[#10c9c0] text-white shadow-lg shadow-[#10c9c0]/25' 
                : 'border-gray-300 text-gray-400 bg-white'
            }`}>
              {step > stepNumber ? (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <span className="text-sm font-medium">{stepNumber}</span>
              )}
            </div>
            {stepNumber < 3 && (
              <div className={`w-12 h-0.5 transition-all duration-300 ${
                step > stepNumber ? 'bg-[#10c9c0]' : 'bg-gray-300'
              }`} />
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );

  const getStepIcon = () => {
    switch (step) {
      case 1:
        return (
          <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
          </svg>
        );
      case 2:
        return (
          <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      case 3:
        return (
          <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
        );
      default:
        return null;
    }
  };

  const getStepTitle = () => {
    switch (step) {
      case 1: return 'Reset Password';
      case 2: return 'Verify Code';
      case 3: return 'New Password';
      default: return '';
    }
  };

  const getStepDescription = () => {
    switch (step) {
      case 1: return 'Enter your email to receive a verification code';
      case 2: return 'Check your email and enter the verification code';
      case 3: return 'Create your new secure password';
      default: return '';
    }
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <form onSubmit={requestVerificationCode} className="space-y-4">
            <InputField
              type="email"
              placeholder="Enter your email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              focused={emailFocused}
              onFocus={() => setEmailFocused(true)}
              onBlur={() => setEmailFocused(false)}
              icon={
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                </svg>
              }
            />
            
            <button
              type="submit"
              disabled={isLoading}
              className={`w-full py-3 px-4 rounded-xl font-medium text-white transition-all duration-200 ${
                isLoading
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-gradient-to-r from-[#10c9c0] to-[#0eb1a9] hover:from-[#0eb1a9] hover:to-[#10c9c0] hover:scale-[1.02] hover:shadow-lg hover:shadow-[#10c9c0]/25 active:scale-[0.98]'
              }`}
            >
              {isLoading ? (
                <div className="flex items-center justify-center space-x-2">
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  <span>Sending...</span>
                </div>
              ) : (
                <div className="flex items-center justify-center space-x-2">
                  <span>Send Reset Code</span>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                  </svg>
                </div>
              )}
            </button>
          </form>
        );

      case 2:
        return (
          <form onSubmit={verifyCode} className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-blue-800">
                A verification code has been sent to{' '}
                <span className="font-medium">{email}</span>
              </p>
            </div>

            <InputField
              type="text"
              placeholder="Enter 6-digit verification code"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value)}
              focused={codeFocused}
              onFocus={() => setCodeFocused(true)}
              onBlur={() => setCodeFocused(false)}
              icon={
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              }
            />

            <div className="flex space-x-3">
              <button
                type="button"
                onClick={() => setStep(1)}
                disabled={isLoading}
                className="flex-1 py-3 px-4 border-2 border-gray-300 text-gray-700 rounded-xl font-medium hover:border-gray-400 hover:bg-gray-50 transition-all duration-200 disabled:opacity-50"
              >
                Back
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className={`flex-1 py-3 px-4 rounded-xl font-medium text-white transition-all duration-200 ${
                  isLoading
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-[#10c9c0] to-[#0eb1a9] hover:from-[#0eb1a9] hover:to-[#10c9c0] hover:scale-[1.02] hover:shadow-lg hover:shadow-[#10c9c0]/25 active:scale-[0.98]'
                }`}
              >
                {isLoading ? (
                  <div className="flex items-center justify-center space-x-2">
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    <span>Verifying...</span>
                  </div>
                ) : (
                  'Verify Code'
                )}
              </button>
            </div>

            <div className="text-center">
              <button
                type="button"
                onClick={requestVerificationCode}
                disabled={isLoading}
                className="text-[#10c9c0] hover:text-[#0eb1a9] font-medium transition-colors duration-200 text-sm relative group"
              >
                Didn't receive the code? Resend
                <span className="absolute left-0 bottom-0 w-0 h-0.5 bg-[#10c9c0] transition-all duration-200 group-hover:w-full"></span>
              </button>
            </div>
          </form>
        );

      case 3:
        return (
          <form onSubmit={resetPassword} className="space-y-4">
            <InputField
              type={showNewPassword ? "text" : "password"}
              placeholder="Enter new password"
              value={newPassword}
              onChange={handleNewPasswordChange}
              focused={newPasswordFocused}
              onFocus={() => setNewPasswordFocused(true)}
              onBlur={() => setNewPasswordFocused(false)}
              showToggle={true}
              showStrength={true}
              passwordStrength={passwordStrength}
              getPasswordStrengthColor={getPasswordStrengthColor}
              getPasswordStrengthText={getPasswordStrengthText}
              onToggle={() => setShowNewPassword(!showNewPassword)}
              icon={
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              }
            />

            <InputField
              type={showConfirmPassword ? "text" : "password"}
              placeholder="Confirm new password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              focused={confirmPasswordFocused}
              onFocus={() => setConfirmPasswordFocused(true)}
              onBlur={() => setConfirmPasswordFocused(false)}
              showToggle={true}
              onToggle={() => setShowConfirmPassword(!showConfirmPassword)}
              icon={
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              }
            />

            {/* Password Match Indicator */}
            {confirmPassword && (
              <div className={`text-sm ${
                newPassword === confirmPassword ? 'text-green-600' : 'text-red-600'
              }`}>
                {newPassword === confirmPassword ? (
                  <div className="flex items-center space-x-1">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Passwords match</span>
                  </div>
                ) : (
                  <div className="flex items-center space-x-1">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    <span>Passwords do not match</span>
                  </div>
                )}
              </div>
            )}

            <div className="flex space-x-3">
              <button
                type="button"
                onClick={() => setStep(2)}
                disabled={isLoading}
                className="flex-1 py-3 px-4 border-2 border-gray-300 text-gray-700 rounded-xl font-medium hover:border-gray-400 hover:bg-gray-50 transition-all duration-200 disabled:opacity-50"
              >
                Back
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className={`flex-1 py-3 px-4 rounded-xl font-medium text-white transition-all duration-200 ${
                  isLoading
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-[#10c9c0] to-[#0eb1a9] hover:from-[#0eb1a9] hover:to-[#10c9c0] hover:scale-[1.02] hover:shadow-lg hover:shadow-[#10c9c0]/25 active:scale-[0.98]'
                }`}
              >
                {isLoading ? (
                  <div className="flex items-center justify-center space-x-2">
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    <span>Resetting...</span>
                  </div>
                ) : (
                  <div className="flex items-center justify-center space-x-2">
                    <span>Reset Password</span>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                )}
              </button>
            </div>
          </form>
        );

      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Step Indicator */}
      <StepIndicator />

      {/* Header */}
      <div className="text-center space-y-2">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-[#10c9c0] to-[#0eb1a9] rounded-2xl mb-4">
          {getStepIcon()}
        </div>
        <h2 className="text-2xl font-bold text-gray-900">{getStepTitle()}</h2>
        <p className="text-gray-600">{getStepDescription()}</p>
      </div>

      {/* Message Box */}
      {message && (
        <div className="animate-in fade-in slide-in-from-top-2 duration-300">
          <MessageBox message={message} type={messageType} />
        </div>
      )}

      {/* Step Content */}
      <div className="transition-all duration-300">
        {renderStep()}
      </div>

      {/* Footer */}
      <div className="text-center pt-4 border-t border-gray-200">
        <button 
          onClick={onToggle} 
          className="text-[#10c9c0] hover:text-[#0eb1a9] font-medium transition-colors duration-200 relative group"
        >
          Remember your password? Sign in
          <span className="absolute left-0 bottom-0 w-0 h-0.5 bg-[#10c9c0] transition-all duration-200 group-hover:w-full"></span>
        </button>
      </div>
    </div>
  );
};

export default ForgetPasswordForm;